import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_BASE = '/api';

// Theme constants (shared with App.js)
const THEME = {
  bg: '#0f1117', bgCard: '#181b27', border: '#1e2130', headerBg: '#13151e',
  text: '#e1e4ea', textMuted: '#8b8fa3', green: '#22c55e', red: '#ef4444',
  blue: '#3b82f6', yellow: '#fbbf24', bgInput: '#1a1d2a', borderInput: '#2a2d3a',
};

const INPUT_STYLE = {
  width: '100%', padding: '10px 14px', fontSize: 14, background: THEME.bgInput,
  border: `1px solid ${THEME.borderInput}`, borderRadius: 6, color: THEME.text,
  outline: 'none', boxSizing: 'border-box',
};

// Password strength checker (Guardrail 3)
function PasswordStrengthLogin({ password }) {
  if (!password) return null;
  const checks = [
    { label: '6+ characters', ok: password.length >= 6 },
    { label: '1 letter', ok: /[a-zA-Z]/.test(password) },
    { label: '1 number', ok: /[0-9]/.test(password) },
    { label: '1 special char', ok: /[^a-zA-Z0-9]/.test(password) },
  ];
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4, marginBottom: 4 }}>
      {checks.map(c => (
        <span key={c.label} style={{ fontSize: 10, color: c.ok ? THEME.green : '#ef4444' }}>
          {c.ok ? '\u2713' : '\u2717'} {c.label}
        </span>
      ))}
    </div>
  );
}

function isStrongPw(pw) {
  return pw && pw.length >= 6 && /[a-zA-Z]/.test(pw) && /[0-9]/.test(pw) && /[^a-zA-Z0-9]/.test(pw);
}

// Standalone component — defined outside LoginScreen so React doesn't
// recreate it on every keystroke (which causes focus loss)
function PasswordField({ id, placeholder, value, onChange, onKeyDown, autoFocus, showPasswords, toggleShow }) {
  const visible = showPasswords[id] || false;
  return (
    <div style={{ position: 'relative' }}>
      <input
        style={{ ...INPUT_STYLE, paddingRight: 40 }}
        type={visible ? 'text' : 'password'}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onKeyDown={onKeyDown}
        autoFocus={autoFocus}
      />
      <button
        type="button"
        onClick={() => toggleShow(id)}
        style={{
          position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
          background: 'none', border: 'none', cursor: 'pointer', padding: 4,
          color: visible ? THEME.text : THEME.textMuted, fontSize: 16, lineHeight: 1,
        }}
        tabIndex={-1}
      >
        {visible ? '🙈' : '👁'}
      </button>
    </div>
  );
}

/**
 * LoginScreen — Handles the entire user authentication flow:
 *   1. First-time setup (create admin account when no users exist)
 *   2. Username + password login
 *   3. Force password change (first login or admin reset or 30-day expiry)
 *   4. TOTP setup (scan QR code, first time only)
 *   5. TOTP verification (6-digit code from authenticator app)
 *
 * Props:
 *   onLogin(sessionId, user) — called when login is fully complete
 */
export default function LoginScreen({ onLogin }) {
  // Which screen to show
  const [step, setStep] = useState('loading'); // loading | setup | login | force-change-password | change-password | setup-totp | verify-totp

  // Form fields
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [totpCode, setTotpCode] = useState('');

  // Setup screen — admin creation
  const [setupUsername, setSetupUsername] = useState('');
  const [setupPassword, setSetupPassword] = useState('');
  const [setupConfirm, setSetupConfirm] = useState('');
  const [setupDisplayName, setSetupDisplayName] = useState('');

  // TOTP setup data
  const [totpSecret, setTotpSecret] = useState('');
  const [totpUri, setTotpUri] = useState('');

  // Password visibility toggles
  const [showPasswords, setShowPasswords] = useState({});
  const toggleShow = (key) => setShowPasswords(prev => ({ ...prev, [key]: !prev[key] }));

  // State tracking
  const [userId, setUserId] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  // Theme (use module-level THEME constant)
  const t = THEME;

  // On mount — check if setup is needed or go to login
  useEffect(() => {
    checkSetupStatus();
  }, []);

  async function checkSetupStatus() {
    try {
      const res = await axios.get(`${API_BASE}/auth/setup-status`);
      if (!res.data.setupComplete) {
        setStep('setup');
      } else {
        setStep('login');
      }
    } catch (err) {
      // Backend may be down
      setStep('login');
      setError('Cannot connect to server. Please try again.');
    }
  }

  // ── First-time admin setup ──
  async function handleSetup() {
    setError('');
    if (!setupUsername.trim() || !setupPassword || !setupDisplayName.trim()) {
      setError('All fields are required');
      return;
    }
    if (setupPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (setupPassword !== setupConfirm) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API_BASE}/auth/setup`, {
        username: setupUsername.trim().toLowerCase(),
        password: setupPassword,
        displayName: setupDisplayName.trim(),
      });
      if (res.data.success) {
        // Admin created — now need to set up TOTP
        setUserId(res.data.user.id);
        setMessage('Admin account created! Now set up two-factor authentication.');
        await startTotpSetup(res.data.user.id);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Setup failed');
    }
    setLoading(false);
  }

  // ── Login (username + password) ──
  async function handleLogin() {
    setError('');
    if (!username.trim() || !password) {
      setError('Username and password are required');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API_BASE}/auth/login`, {
        username: username.trim().toLowerCase(),
        password,
      });

      if (res.data.success) {
        setUserId(res.data.userId);
        const next = res.data.nextStep;

        if (next === 'force-change-password') {
          setMessage(res.data.message);
          setStep('force-change-password');
        } else if (next === 'change-password') {
          setMessage(res.data.message);
          setStep('change-password');
        } else if (next === 'setup-totp') {
          setMessage('Please set up two-factor authentication.');
          await startTotpSetup(res.data.userId);
        } else if (next === 'verify-totp') {
          setStep('verify-totp');
        }
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed');
    }
    setLoading(false);
  }

  // ── Force change password (no current password needed) ──
  async function handleForceChangePassword() {
    setError('');
    if (!newPassword || !confirmPassword) {
      setError('Please fill in both fields');
      return;
    }
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API_BASE}/auth/force-change-password`, {
        userId,
        newPassword,
      });
      if (res.data.success) {
        setNewPassword('');
        setConfirmPassword('');
        if (res.data.nextStep === 'setup-totp') {
          setMessage('Password set! Now set up two-factor authentication.');
          await startTotpSetup(userId);
        } else {
          setStep('verify-totp');
        }
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Password change failed');
    }
    setLoading(false);
  }

  // ── Change password (knows current password — 30-day expiry) ──
  async function handleChangePassword() {
    setError('');
    if (!password || !newPassword || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }
    if (newPassword.length < 6) {
      setError('New password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API_BASE}/auth/change-password`, {
        userId,
        currentPassword: password,
        newPassword,
      });
      if (res.data.success) {
        setPassword('');
        setNewPassword('');
        setConfirmPassword('');
        if (res.data.nextStep === 'setup-totp') {
          setMessage('Password changed! Now set up two-factor authentication.');
          await startTotpSetup(userId);
        } else {
          setStep('verify-totp');
        }
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Password change failed');
    }
    setLoading(false);
  }

  // ── TOTP setup — get QR code ──
  async function startTotpSetup(uid) {
    try {
      const res = await axios.post(`${API_BASE}/auth/totp/setup`, { userId: uid });
      setTotpSecret(res.data.secret);
      setTotpUri(res.data.uri);
      setStep('setup-totp');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to start 2FA setup');
    }
  }

  // ── TOTP setup — verify first code ──
  async function handleVerifyTotpSetup() {
    setError('');
    if (!totpCode || totpCode.length !== 6) {
      setError('Enter the 6-digit code from your authenticator app');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API_BASE}/auth/totp/verify-setup`, {
        userId,
        code: totpCode,
      });
      if (res.data.success) {
        // Login complete!
        onLogin(res.data.sessionId, res.data.user);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid code. Try again.');
    }
    setTotpCode('');
    setLoading(false);
  }

  // ── TOTP verification (every login) ──
  async function handleVerifyTotp() {
    setError('');
    if (!totpCode || totpCode.length !== 6) {
      setError('Enter the 6-digit code from your authenticator app');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API_BASE}/auth/verify-totp`, {
        userId,
        code: totpCode,
      });
      if (res.data.success) {
        // Login complete!
        onLogin(res.data.sessionId, res.data.user);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid code. Try again.');
    }
    setTotpCode('');
    setLoading(false);
  }

  // ═══════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════

  const cardStyle = {
    width: 400,
    background: t.bgCard,
    borderRadius: 10,
    border: `1px solid ${t.border}`,
    overflow: 'hidden',
  };

  const inputStyle = INPUT_STYLE;

  const buttonStyle = {
    width: '100%',
    padding: '10px',
    fontSize: 13,
    fontWeight: 600,
    background: t.blue,
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    cursor: loading ? 'wait' : 'pointer',
    opacity: loading ? 0.6 : 1,
  };

  const wrapperStyle = {
    fontFamily: "'Segoe UI', sans-serif",
    background: t.bg,
    color: t.text,
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  // ── Loading ──
  if (step === 'loading') {
    return (
      <div style={wrapperStyle}>
        <div style={{ color: t.textMuted, fontSize: 13 }}>Loading...</div>
      </div>
    );
  }

  // ── First-time admin setup ──
  if (step === 'setup') {
    return (
      <div style={wrapperStyle}>
        <div style={cardStyle}>
          <div style={{ padding: '28px 24px 18px', borderBottom: `1px solid ${t.border}`, textAlign: 'center' }}>
            <div style={{ fontSize: 20, fontWeight: 700 }}>Trading App</div>
            <div style={{ color: t.textMuted, fontSize: 12, marginTop: 6 }}>First time setup — create your admin account</div>
          </div>
          <div style={{ padding: '20px 24px' }}>
            {error && <div style={{ padding: '8px 12px', background: '#2e1a1a', borderRadius: 6, fontSize: 12, color: t.red, marginBottom: 12 }}>{error}</div>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <input style={inputStyle} placeholder="Display name (e.g., Shubh Agarwal)" value={setupDisplayName} onChange={e => setSetupDisplayName(e.target.value)} autoFocus />
              <input style={inputStyle} placeholder="Username (e.g., shubh)" value={setupUsername} onChange={e => setSetupUsername(e.target.value)} />
              <PasswordField id="setup-pw" placeholder="Password (min 6 characters)" value={setupPassword} onChange={e => setSetupPassword(e.target.value)} showPasswords={showPasswords} toggleShow={toggleShow} />
              <PasswordStrengthLogin password={setupPassword} />
              <PasswordField id="setup-confirm" placeholder="Confirm password" value={setupConfirm} onChange={e => setSetupConfirm(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSetup()} showPasswords={showPasswords} toggleShow={toggleShow} />
              <button style={buttonStyle} onClick={handleSetup} disabled={loading}>{loading ? 'Creating...' : 'Create Admin Account'}</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Login ──
  if (step === 'login') {
    return (
      <div style={wrapperStyle}>
        <div style={cardStyle}>
          <div style={{ padding: '28px 24px 18px', borderBottom: `1px solid ${t.border}`, textAlign: 'center' }}>
            <div style={{ fontSize: 20, fontWeight: 700 }}>Trading App</div>
            <div style={{ color: t.textMuted, fontSize: 12, marginTop: 6 }}>Login to continue</div>
          </div>
          <div style={{ padding: '20px 24px' }}>
            {error && <div style={{ padding: '8px 12px', background: '#2e1a1a', borderRadius: 6, fontSize: 12, color: t.red, marginBottom: 12 }}>{error}</div>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <input style={inputStyle} placeholder="Username" value={username} onChange={e => setUsername(e.target.value)} autoFocus />
              <PasswordField id="login-pw" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleLogin()} showPasswords={showPasswords} toggleShow={toggleShow} />
              <button style={buttonStyle} onClick={handleLogin} disabled={loading}>{loading ? 'Logging in...' : 'Login'}</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Force change password (first login / admin reset) ──
  if (step === 'force-change-password') {
    return (
      <div style={wrapperStyle}>
        <div style={cardStyle}>
          <div style={{ padding: '28px 24px 18px', borderBottom: `1px solid ${t.border}`, textAlign: 'center' }}>
            <div style={{ fontSize: 20, fontWeight: 700 }}>Set New Password</div>
            <div style={{ color: t.yellow, fontSize: 12, marginTop: 6 }}>{message || 'You must set a new password before continuing.'}</div>
          </div>
          <div style={{ padding: '20px 24px' }}>
            {error && <div style={{ padding: '8px 12px', background: '#2e1a1a', borderRadius: 6, fontSize: 12, color: t.red, marginBottom: 12 }}>{error}</div>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <PasswordField id="force-new" placeholder="New password (min 6 characters)" value={newPassword} onChange={e => setNewPassword(e.target.value)} autoFocus showPasswords={showPasswords} toggleShow={toggleShow} />
              <PasswordStrengthLogin password={newPassword} />
              <PasswordField id="force-confirm" placeholder="Confirm new password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleForceChangePassword()} showPasswords={showPasswords} toggleShow={toggleShow} />
              <button style={buttonStyle} onClick={handleForceChangePassword} disabled={loading}>{loading ? 'Setting...' : 'Set Password'}</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Change password (30-day expiry — needs current password) ──
  if (step === 'change-password') {
    return (
      <div style={wrapperStyle}>
        <div style={cardStyle}>
          <div style={{ padding: '28px 24px 18px', borderBottom: `1px solid ${t.border}`, textAlign: 'center' }}>
            <div style={{ fontSize: 20, fontWeight: 700 }}>Password Expired</div>
            <div style={{ color: t.yellow, fontSize: 12, marginTop: 6 }}>{message || 'Your password has expired. Please set a new one.'}</div>
          </div>
          <div style={{ padding: '20px 24px' }}>
            {error && <div style={{ padding: '8px 12px', background: '#2e1a1a', borderRadius: 6, fontSize: 12, color: t.red, marginBottom: 12 }}>{error}</div>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <PasswordField id="change-current" placeholder="Current password" value={password} onChange={e => setPassword(e.target.value)} autoFocus showPasswords={showPasswords} toggleShow={toggleShow} />
              <PasswordField id="change-new" placeholder="New password (min 6 characters)" value={newPassword} onChange={e => setNewPassword(e.target.value)} showPasswords={showPasswords} toggleShow={toggleShow} />
              <PasswordStrengthLogin password={newPassword} />
              <PasswordField id="change-confirm" placeholder="Confirm new password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleChangePassword()} showPasswords={showPasswords} toggleShow={toggleShow} />
              <button style={buttonStyle} onClick={handleChangePassword} disabled={loading}>{loading ? 'Changing...' : 'Change Password'}</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── TOTP setup (first time — show QR code) ──
  if (step === 'setup-totp') {
    return (
      <div style={wrapperStyle}>
        <div style={cardStyle}>
          <div style={{ padding: '28px 24px 18px', borderBottom: `1px solid ${t.border}`, textAlign: 'center' }}>
            <div style={{ fontSize: 20, fontWeight: 700 }}>Set Up 2FA</div>
            <div style={{ color: t.textMuted, fontSize: 12, marginTop: 6 }}>Scan this code with Microsoft Authenticator</div>
          </div>
          <div style={{ padding: '20px 24px' }}>
            {error && <div style={{ padding: '8px 12px', background: '#2e1a1a', borderRadius: 6, fontSize: 12, color: t.red, marginBottom: 12 }}>{error}</div>}
            {message && <div style={{ padding: '8px 12px', background: '#1a2e1a', borderRadius: 6, fontSize: 12, color: t.green, marginBottom: 12 }}>{message}</div>}

            {/* QR code placeholder — we'll use the URI to generate a QR image */}
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(totpUri)}`}
                alt="QR Code"
                style={{ width: 200, height: 200, borderRadius: 8, background: '#fff', padding: 8 }}
              />
            </div>

            <div style={{ fontSize: 11, color: t.textMuted, textAlign: 'center', marginBottom: 12 }}>
              Or enter this key manually: <span style={{ fontFamily: 'monospace', color: t.text, fontSize: 12, userSelect: 'all' }}>{totpSecret}</span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <input
                style={{ ...inputStyle, letterSpacing: 8, textAlign: 'center', fontSize: 18, fontWeight: 700 }}
                placeholder="6-digit code"
                value={totpCode}
                onChange={e => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                maxLength={6}
                autoFocus
                onKeyDown={e => e.key === 'Enter' && handleVerifyTotpSetup()}
              />
              <button style={buttonStyle} onClick={handleVerifyTotpSetup} disabled={loading}>{loading ? 'Verifying...' : 'Verify & Complete Setup'}</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── TOTP verification (every login) ──
  if (step === 'verify-totp') {
    return (
      <div style={wrapperStyle}>
        <div style={cardStyle}>
          <div style={{ padding: '28px 24px 18px', borderBottom: `1px solid ${t.border}`, textAlign: 'center' }}>
            <div style={{ fontSize: 20, fontWeight: 700 }}>Two-Factor Authentication</div>
            <div style={{ color: t.textMuted, fontSize: 12, marginTop: 6 }}>Enter the code from your authenticator app</div>
          </div>
          <div style={{ padding: '20px 24px' }}>
            {error && <div style={{ padding: '8px 12px', background: '#2e1a1a', borderRadius: 6, fontSize: 12, color: t.red, marginBottom: 12 }}>{error}</div>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <input
                style={{ ...inputStyle, letterSpacing: 8, textAlign: 'center', fontSize: 18, fontWeight: 700 }}
                placeholder="6-digit code"
                value={totpCode}
                onChange={e => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                maxLength={6}
                autoFocus
                onKeyDown={e => e.key === 'Enter' && handleVerifyTotp()}
              />
              <button style={buttonStyle} onClick={handleVerifyTotp} disabled={loading}>{loading ? 'Verifying...' : 'Verify'}</button>
            </div>
            <button
              onClick={() => { setStep('login'); setError(''); setTotpCode(''); setUserId(null); }}
              style={{ marginTop: 12, fontSize: 11, color: t.textMuted, background: 'none', border: 'none', cursor: 'pointer', width: '100%', textAlign: 'center' }}
            >
              Back to login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
